import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppData } from '../app/AppContext';
import { parseReportPeriod, type ReportPeriod } from './report-format';

export const HISTORY_SEARCH_MAX_LENGTH = 120;
export function normalizeActivityQuery(value: string): string { return value.slice(0, HISTORY_SEARCH_MAX_LENGTH); }

export interface ActivityState {
  mode: 'history' | 'reports';
  period: ReportPeriod;
  anchor: string;
  query: string;
  typeFilter: '' | 'INCOME' | 'EXPENSE' | 'TRANSFER';
  includeCancelled: boolean;
  accountId: string;
  debtCategoryId: string;
  authorId: string;
  limit: number;
  cursor?: string;
  cursorHistory: (string | undefined)[];
}

export function workspaceToday(timezone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  return `${parts.find(p => p.type === 'year')!.value}-${parts.find(p => p.type === 'month')!.value}-${parts.find(p => p.type === 'day')!.value}`;
}

export function parseActivityState(params: URLSearchParams, today: string): ActivityState {
  const anchor = params.get('anchor') ?? '';
  const validDate = /^\d{4}-\d{2}-\d{2}$/u.test(anchor) && Number.isFinite(Date.parse(`${anchor}T12:00:00Z`)) && new Date(`${anchor}T12:00:00Z`).toISOString().slice(0, 10) === anchor;
  const type = params.get('type');
  const id = (key: string) => /^[\da-f]{8}-(?:[\da-f]{4}-){3}[\da-f]{12}$/iu.test(params.get(key) ?? '') ? params.get(key)! : '';
  const limit = Number(params.get('limit') ?? 50);
  return { mode: params.get('mode') === 'reports' ? 'reports' : 'history', period: parseReportPeriod(params.get('period')), anchor: validDate ? anchor : today,
    query: normalizeActivityQuery(params.get('q') ?? ''), typeFilter: type === 'INCOME' || type === 'EXPENSE' || type === 'TRANSFER' ? type : '', includeCancelled: params.get('includeCancelled') === 'true',
    accountId: id('accountId'), debtCategoryId: id('debtCategoryId'), authorId: id('authorId'), limit: Number.isInteger(limit) && limit > 0 && limit <= 200 ? limit : 50,
    cursor: params.get('cursor') || undefined, cursorHistory: [],
  };
}

export function activityPath(state: ActivityState, overlay?: string | null): string {
  const params = new URLSearchParams({ mode: state.mode, period: state.period, anchor: state.anchor });
  const query = normalizeActivityQuery(state.query);
  if (query) params.set('q', query);
  if (state.typeFilter) params.set('type', state.typeFilter);
  if (state.includeCancelled) params.set('includeCancelled', 'true');
  for (const key of ['accountId', 'debtCategoryId', 'authorId'] as const) if (state[key]) params.set(key, state[key]);
  if (state.limit !== 50) params.set('limit', String(state.limit));
  if (state.cursor) params.set('cursor', state.cursor);
  if (overlay && ['report-period', 'history-filters', 'workspace'].includes(overlay)) params.set('overlay', overlay);
  return `/activity?${params}`;
}

// Return targets carry the entire local URL, including nested detail context.
// Normalize with URL to reject dot-segment/auth escapes before allowlisting.
export function safeActivityReturnTo(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || [...value].some(char => char === '\\' || char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127) || !value.startsWith('/') || value.startsWith('//')) return fallback;
  const url = new URL(value, 'https://activity.invalid');
  if (url.origin !== 'https://activity.invalid' || !/^\/(activity|history|reports|dashboard|debts(?:\/(?:categories|movements)\/[^/]+(?:\/edit)?)?|transactions\/[^/]+|categories\/[^/]+)(?:\/)?$/u.test(url.pathname)) return fallback;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function initialActivityState(pathname: string, search: string, today: string): ActivityState {
  for (let depth = 0; depth < 5; depth += 1) {
    const params = new URLSearchParams(search);
    if (['/activity', '/history', '/reports'].includes(pathname)) {
      if (pathname === '/history') params.set('mode', 'history');
      if (pathname === '/reports') params.set('mode', 'reports');
      return parseActivityState(params, today);
    }
    const target = safeActivityReturnTo(params.get('detailReturnTo') ?? params.get('returnTo') ?? params.get('returnPath'), '');
    if (!target) break;
    const url = new URL(target, 'https://activity.invalid');
    pathname = url.pathname;
    search = url.search;
  }
  return parseActivityState(new URLSearchParams(), today);
}

type ActivityContextValue = ActivityState & { setState: (next: Partial<ActivityState>, options?: { replace?: boolean; overlay?: string | null }) => void; path: string };
const ActivityContext = createContext<ActivityContextValue | undefined>(undefined);

export function ActivityStateProvider({ children }: { children: ReactNode }) {
  const { workspace } = useAppData();
  const location = useLocation();
  const navigate = useNavigate();
  const today = workspaceToday(workspace?.timezone ?? 'Europe/Moscow');
  const [stored, setStored] = useState<{ workspaceId?: string; state: ActivityState; resetSearch?: string }>(() => ({ workspaceId: workspace?.id, state: initialActivityState(location.pathname, location.search, today) }));
  const changed = stored.workspaceId !== workspace?.id;
  const active = location.pathname === '/activity';
  const base = changed ? parseActivityState(new URLSearchParams(), today) : stored.state;
  const parsed = active && !changed && stored.resetSearch !== location.search ? parseActivityState(new URLSearchParams(location.search), today) : base;
  const navigationState = (typeof location.state === 'object' && location.state !== null ? location.state : {}) as Record<string, unknown>;
  const page = typeof navigationState.activityPage === 'object' && navigationState.activityPage !== null ? navigationState.activityPage as { workspaceId?: unknown; cursor?: unknown; previous?: unknown } : undefined;
  const routeHistory = active && page !== undefined && page.workspaceId === workspace?.id && page.cursor === parsed.cursor && Array.isArray(page.previous) && page.previous.every(value => value === undefined || typeof value === 'string') ? page.previous as (string | undefined)[] : undefined;
  const state = { ...parsed, cursorHistory: routeHistory ?? (parsed.cursor === base.cursor ? base.cursorHistory : []) };
  const overlay = changed || stored.resetSearch === location.search ? null : new URLSearchParams(location.search).get('overlay');
  const path = activityPath(state);
  const canonical = activityPath(state, overlay);
  // Adjust only this owner's state during render when its route/context changes.
  // A workspace reset ignores the old URL until its replacement has committed.
  if (changed) setStored({ workspaceId: workspace?.id, state, resetSearch: active ? location.search : undefined });
  else if (JSON.stringify(stored.state) !== JSON.stringify(state)) setStored({ workspaceId: workspace?.id, state });
  // Back/deep-link URL is authoritative while Activity is open.
  useEffect(() => {
    if (active && `${location.pathname}${location.search}` !== canonical) navigate(canonical, { replace: true, state: location.state });
  }, [active, canonical, location.pathname, location.search, location.state, navigate]);
  const setState: ActivityContextValue['setState'] = (next, options) => {
    const result = { ...state, ...next, query: normalizeActivityQuery(next.query ?? state.query) };
    if (['period', 'anchor', 'query', 'typeFilter', 'includeCancelled', 'accountId', 'debtCategoryId', 'authorId', 'limit'].some(key => key in next)) { result.cursor = undefined; result.cursorHistory = []; }
    setStored({ workspaceId: workspace?.id, state: result });
    if (active) navigate(activityPath(result, options?.overlay === undefined ? overlay : options.overlay), { replace: options?.replace, state: { ...navigationState, activityPage: { workspaceId: workspace?.id, cursor: result.cursor, previous: result.cursorHistory } } });
  };
  return <ActivityContext.Provider value={{ ...state, setState, path }}>{children}</ActivityContext.Provider>;
}

export function useActivityState(): ActivityContextValue {
  const value = useContext(ActivityContext);
  if (!value) throw new Error('ActivityStateProvider is missing');
  return value;
}
